// vim: set ts=2 sts=2 sw=2 et:

import React from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { QRCodeSVG } from "qrcode.react";
import {
  faBolt,
  faCircleCheck,
  faCopy,
  faLink,
  faLock,
  faPlug,
  faPowerOff,
  faQrcode,
  faRotateRight,
  faServer,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { requestOperatorNotificationPermission } from "../operation/operatorAttention";

import { GlobalState, MeetState, LiftingState } from "../../types/stateTypes";
import { JUDGE_POSITIONS, JudgePosition } from "../judging/resolveJudgeVotes";
import {
  activatePlatformLiveProfile,
  clearHelperProfileCandidate,
  deactivatePlatformLiveProfile,
  setPlatformLiveBridgeProvider,
  setRegistrationPaginationEnabled,
  setRecordTestToolsEnabled,
} from "./platformLiveProfileActions";
import { defaultPlatformLiveFeatures, PlatformLiveFeatures, PlatformLiveProfile } from "./platformLiveProfile";
import {
  PlatformLiveBridgeRequestError,
  PlatformLiveBridgeStatus,
  getDefaultPlatformLiveBridgeProviderId,
  getPlatformLiveBridgeProviderLabel,
  getPlatformLiveBridgeProviderOptions,
  getPlatformLiveBridgeStatus,
  getPlatformLiveBridgeConfig,
  getPlatformLiveBridgeProviders,
  isRemotePlatformLiveBridge,
  startPlatformLiveBridge,
  stopPlatformLiveBridge,
} from "./platformLiveBridgeClient";
import type { PlatformLiveBridgeProviderOption } from "./platformLiveBridgeClient";
import type { PlatformLiveBridgeProvider, PlatformLiveBridgeProviderId } from "./platformLiveBridgeClient";
import {
  buildPlatformDmxBridgeCommand,
  buildPlatformLiveCommand,
  buildPlatformLiveUrls,
  buildPlatformLiveUrlsForRemoteBridge,
  makeDefaultDmxConfig,
  makePlatformLiveProfile,
  makePlatformLiveProfileDraft,
  makePlatformLiveRuntimeKey,
  OverlayBackground,
  withOverlayBackground,
} from "./platformLiveLauncher";
import { choosePreferredLanHost, isLoopbackHost } from "./lanAccessibleUrl";
import {
  DEFAULT_DMX_HELPER_URL,
  ETDMX_NATIVE_WEB_URL,
  createBrowserDmxHelperClient,
  isDmxHelperRoomAssigned,
  isDmxHelperRoomVerified,
  withDmxHelperConnection,
} from "./browserDmxHelperClient";
import type { DmxHelperPublicStatus } from "../dmxHelper/connection";
import Switch from "../../components/common/Switch";
import styles from "./PlatformLiveLauncher.module.scss";

interface StateProps {
  meet: MeetState;
  lifting: LiftingState;
  activeProfile: PlatformLiveProfile | null;
  helperProfileCandidate: PlatformLiveProfile | null;
  tournamentId: string | null;
  recordTestToolsEnabled: boolean;
  registrationPaginationEnabled: boolean;
  bridgeProviderId: PlatformLiveBridgeProviderId | null;
}

interface DispatchProps {
  activateProfile: (profile: PlatformLiveProfile) => void;
  clearHelperProfileCandidate: () => void;
  deactivateProfile: () => void;
  setRecordTestTools: (enabled: boolean) => void;
  setRegistrationPagination: (enabled: boolean) => void;
  setBridgeProvider: (providerId: PlatformLiveBridgeProviderId) => void;
}

type Props = StateProps & DispatchProps;
type BridgeAction = "idle" | "starting" | "stopping" | "restarting";

const BRIDGE_RESTART_DEBOUNCE_MS = 500;

const getInitialLanHost = (): string => {
  if (typeof window === "undefined") {
    return "127.0.0.1";
  }
  return window.location.hostname || "127.0.0.1";
};

const getInitialAppPort = (): number => {
  if (typeof window === "undefined" || window.location.port === "") {
    return 5000;
  }
  const parsed = Number(window.location.port);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 5000;
};

const getCloudNetworkDefaults = (): { lanHost: string; appPort: number; livePort: number } => {
  if (typeof window === "undefined") {
    return { lanHost: "localhost", appPort: 443, livePort: 443 };
  }
  const parsedPort = Number(window.location.port);
  const appPort =
    Number.isInteger(parsedPort) && parsedPort > 0 ? parsedPort : window.location.protocol === "https:" ? 443 : 80;
  return {
    lanHost: window.location.hostname || "localhost",
    appPort,
    livePort: appPort,
  };
};

const renderBridgeModeDetails = (
  bridgeMode: boolean,
  bridgeUrl: string | undefined,
  providerOptions: ReadonlyArray<PlatformLiveBridgeProviderOption>,
  providerId: PlatformLiveBridgeProviderId | null,
  providerDisabled: boolean,
  onProviderChange: (providerId: PlatformLiveBridgeProviderId) => void,
) => {
  if (!bridgeMode) {
    return (
      <div className={styles.modeManual}>
        <strong>Modo manual</strong>
        <span>Activar guarda el perfil y deja el comando disponible como fallback manual.</span>
      </div>
    );
  }

  return (
    <>
      <div className={styles.modeAuto}>
        <span>El bridge inicia y detiene el Platform Live Server solo al pulsar Activar o Desactivar.</span>
      </div>
      <div className="form-group">
        <label htmlFor="platform-live-bridge-provider">Proveedor</label>
        <select
          id="platform-live-bridge-provider"
          aria-label="Proveedor Platform Live"
          className="form-control"
          value={providerId || ""}
          disabled={providerDisabled}
          onChange={(event) => onProviderChange(event.target.value as PlatformLiveBridgeProviderId)}
        >
          {providerOptions.map((option) => (
            <option key={option.id} value={option.id} disabled={!option.configured}>
              {option.label}
              {option.configured ? "" : " (sin configurar)"}
            </option>
          ))}
        </select>
        <small>Desactivá la sala antes de cambiar de proveedor.</small>
      </div>
      {bridgeUrl !== undefined ? <p>Bridge: {bridgeUrl}</p> : null}
    </>
  );
};

// One option card of the launcher's feature grid; the card is highlighted while it's on.
const FeatureSwitch = (props: {
  id: string;
  checked: boolean;
  onChange: () => void;
  label: React.ReactNode;
  description: string;
}) => (
  <div className={`${styles.featureToggle} ${props.checked ? styles.featureToggleOn : ""}`}>
    <Switch
      id={props.id}
      checked={props.checked}
      onChange={props.onChange}
      label={props.label}
      description={props.description}
    />
  </div>
);

const RecordTestToolsToggle = ({ enabled, onChange }: { enabled: boolean; onChange: (enabled: boolean) => void }) => (
  <div className={styles.developerToggle}>
    <Switch
      id="platform-live-record-test-tools"
      ariaLabel="Mostrar herramientas de prueba en Récords"
      checked={enabled}
      onChange={() => onChange(!enabled)}
      label="Herramientas de prueba en Récords"
      description="Muestra en Récords los controles para generar, abrir y eliminar intentos de prueba. Apagalo en el torneo real."
    />
  </div>
);

const RegistrationPaginationToggle = ({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
}) => (
  <div className={styles.developerToggle}>
    <Switch
      id="platform-live-registration-pagination"
      ariaLabel="Paginación en Registro y Pesaje"
      checked={enabled}
      onChange={() => onChange(!enabled)}
      label="Paginación en Registro y Pesaje"
      description="Muestra Inscripciones y Pesaje de a 25 atletas por página, con botones para cambiar de página. Apagado, se ven todos juntos."
    />
  </div>
);

const IntegratedJudgesPanelAction = ({
  enabled,
  copied,
  onOpen,
  onCopy,
}: {
  enabled: boolean;
  copied: boolean;
  onOpen: () => void;
  onCopy: () => void;
}) => (
  <div className={styles.developerAction}>
    <div className={styles.developerActions}>
      <button
        type="button"
        className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`}
        disabled={!enabled}
        onClick={onOpen}
      >
        <FontAwesomeIcon icon={faUpRightFromSquare} className={styles.buttonIcon} />
        Abrir panel de 3 jueces
      </button>
      <button
        type="button"
        className={`btn btn-outline-primary btn-sm ${styles.actionButton}`}
        disabled={!enabled}
        onClick={onCopy}
      >
        <FontAwesomeIcon icon={faCopy} className={styles.buttonIcon} />
        Copiar enlace
      </button>
    </div>
    {copied ? <div className={styles.copyFeedback}>Enlace copiado en el portapapeles</div> : null}
    <small>
      {enabled
        ? "Abre las tres posiciones con datos y acciones reales."
        : "Disponible al activar una sala con Votación de jueces."}
    </small>
  </div>
);

const maxPlatformsForDay = (meet: MeetState, day: number): number => {
  return meet.platformsOnDays[day - 1] || 1;
};

const makePlatformOptions = (meet: MeetState, day: number): Array<number> => {
  const count = maxPlatformsForDay(meet, day);
  const platforms = [];
  for (let platform = 1; platform <= count; platform++) {
    platforms.push(platform);
  }
  return platforms;
};

const makeDayOptions = (meet: MeetState): Array<number> => {
  const days = [];
  for (let day = 1; day <= meet.lengthDays; day++) {
    days.push(day);
  }
  return days;
};

const JUDGE_LABELS: Record<JudgePosition, string> = {
  J1: "Juez Lateral Izquierdo",
  J2: "Juez Central",
  J3: "Juez Lateral Derecho",
};

const copyToClipboard = (url: string): boolean => {
  if (typeof navigator === "undefined") return false;
  if (navigator.clipboard !== undefined) {
    navigator.clipboard.writeText(url).catch(() => undefined);
    return true;
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = url;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};

const formatBridgeError = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

const getBridgeErrorStatus = (error: unknown) => {
  return error instanceof PlatformLiveBridgeRequestError ? error.bridgeStatus : null;
};

const QR_DISPLAY_SIZE = 1024;

const OVERLAY_BACKGROUND_STORAGE_KEY = "wrpf.overlayBackground";

const ClientLinkCard = ({
  label,
  url,
  qrVisible,
  copied,
  onCopy,
  onToggleQr,
}: {
  label: string;
  url: string;
  qrVisible: boolean;
  copied: boolean;
  onCopy: () => void;
  onToggleQr: () => void;
}) => (
  <div className={styles.roleCard}>
    <div className={styles.roleHeader}>
      <div className={styles.roleIcon}>
        <FontAwesomeIcon icon={faLink} />
      </div>
      <div>
        <h3 className={styles.roleTitle}>{label}</h3>
      </div>
    </div>
    <div className={styles.roleActions}>
      <button type="button" className={`btn btn-outline-primary btn-sm ${styles.actionButton}`} onClick={onCopy}>
        <FontAwesomeIcon icon={faCopy} className={styles.buttonIcon} />
        Copiar enlace
      </button>
      <button type="button" className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`} onClick={onToggleQr}>
        <FontAwesomeIcon icon={faQrcode} className={styles.buttonIcon} />
        {qrVisible ? "Ocultar QR" : "Mostrar QR"}
      </button>
      <button
        type="button"
        className={`btn btn-outline-secondary btn-sm ${styles.actionButton} ${styles.iconOnlyButton}`}
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
        aria-label="Abrir en nueva pestaña"
        title="Abrir en nueva pestaña"
      >
        <FontAwesomeIcon icon={faUpRightFromSquare} />
      </button>
    </div>
    {copied ? <div className={styles.copyFeedback}>Enlace copiado en el portapapeles</div> : null}
    {qrVisible && typeof document !== "undefined"
      ? ReactDOM.createPortal(
          <div
            className={styles.qrOverlay}
            role="dialog"
            aria-modal="true"
            aria-label={`QR ${label}`}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                onToggleQr();
              }
            }}
          >
            <div className={styles.qrOverlayContent}>
              <div className={styles.qrOverlayLabel}>{label}</div>
              <div className={styles.qrOverlayCode} data-qr-code="true">
                <QRCodeSVG
                  value={url}
                  size={QR_DISPLAY_SIZE}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={true}
                />
              </div>
              <div className={styles.qrOverlayHint}>Escaneá con tu teléfono</div>
              <button
                type="button"
                className={`btn btn-outline-light btn-sm ${styles.actionButton}`}
                onClick={onToggleQr}
              >
                Cerrar
              </button>
            </div>
          </div>,
          document.body,
        )
      : null}
  </div>
);

const PlatformLiveLauncherContainer = ({
  meet,
  lifting,
  activeProfile,
  helperProfileCandidate,
  tournamentId,
  recordTestToolsEnabled,
  registrationPaginationEnabled,
  bridgeProviderId,
  activateProfile,
  clearHelperProfileCandidate,
  deactivateProfile,
  setRecordTestTools,
  setRegistrationPagination,
  setBridgeProvider,
}: Props) => {
  const initialDay = activeProfile?.day || lifting.day;
  const initialPlatform = activeProfile?.platformId || lifting.platform;
  const initialFeatures = { ...defaultPlatformLiveFeatures(), ...(activeProfile?.features || {}) };
  const [day, setDay] = React.useState(initialDay);
  const [platformId, setPlatformId] = React.useState(Math.min(initialPlatform, maxPlatformsForDay(meet, initialDay)));
  const [lanHost, setLanHost] = React.useState(activeProfile?.lanHost || getInitialLanHost());
  const [appPort, setAppPort] = React.useState(activeProfile?.appPort || getInitialAppPort());
  const [livePort, setLivePort] = React.useState(activeProfile?.livePort || 4510 + platformId);
  const [features, setFeatures] = React.useState<PlatformLiveFeatures>(initialFeatures);
  const [visibleQr, setVisibleQr] = React.useState<Record<string, boolean>>({});
  const [copiedLink, setCopiedLink] = React.useState<string | null>(null);
  // Fondo del overlay: blanco por defecto; la elección se recuerda en este navegador.
  const [overlayBackground, setOverlayBackground] = React.useState<OverlayBackground>(() => {
    try {
      return window.localStorage.getItem(OVERLAY_BACKGROUND_STORAGE_KEY) === "transparente" ? "transparente" : "blanco";
    } catch (_error) {
      return "blanco";
    }
  });
  const changeOverlayBackground = (background: OverlayBackground) => {
    setOverlayBackground(background);
    try {
      window.localStorage.setItem(OVERLAY_BACKGROUND_STORAGE_KEY, background);
    } catch (_error) {
      /* sin almacenamiento: vale solo para esta sesión */
    }
  };

  React.useEffect(() => {
    if (copiedLink === null) return;
    const timer = window.setTimeout(() => {
      setCopiedLink(null);
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [copiedLink]);

  const [devDetailsOpen, setDevDetailsOpen] = React.useState(false);
  const [configDevDetailsOpen, setConfigDevDetailsOpen] = React.useState(false);
  const [bridgeAction, setBridgeAction] = React.useState<BridgeAction>("idle");
  const [bridgeError, setBridgeError] = React.useState<string | null>(null);

  // Un puerto ocupado se arregla en "Configuraciones avanzadas": la abrimos para que se vea dónde.
  React.useEffect(() => {
    if (bridgeError !== null && /port|puerto/i.test(bridgeError)) {
      setConfigDevDetailsOpen(true);
    }
  }, [bridgeError]);
  const [helperStatus, setHelperStatus] = React.useState<DmxHelperPublicStatus | null>(null);
  // Estado REAL del Platform Live Server (poleado), vs `activeProfile` que es sólo un flag local.
  const [liveStatus, setLiveStatus] = React.useState<"unknown" | "running" | "stopped" | "unreachable">("unknown");
  const bridgeProviders = React.useMemo(() => getPlatformLiveBridgeProviders(), []);
  const bridgeProviderOptions = React.useMemo(() => getPlatformLiveBridgeProviderOptions(), []);
  const selectedBridgeProviderId =
    bridgeProviders.some((provider) => provider.id === bridgeProviderId)
      ? bridgeProviderId
      : getDefaultPlatformLiveBridgeProviderId();
  const bridgeConfig = React.useMemo(
    () => getPlatformLiveBridgeConfig(selectedBridgeProviderId),
    [selectedBridgeProviderId],
  );
  const activeBridgeConfig = React.useMemo(
    () => (activeProfile === null ? bridgeConfig : getPlatformLiveBridgeConfig(activeProfile.bridgeProviderId)),
    [activeProfile, bridgeConfig],
  );
  const bridgeMode = bridgeConfig !== null;
  const cloudBridgeMode = bridgeConfig !== null && isRemotePlatformLiveBridge(bridgeConfig);
  const activeCloudBridgeMode = activeBridgeConfig !== null && isRemotePlatformLiveBridge(activeBridgeConfig);
  const selectedHelperProvider = bridgeProviders.find((provider) => provider.id === selectedBridgeProviderId) || null;
  const activeHelperProvider =
    bridgeProviders.find((provider) => provider.id === activeProfile?.bridgeProviderId) || null;
  const helperCloudDmxMode = cloudBridgeMode && features.dmx && selectedHelperProvider !== null;
  const activeHelperMode = activeProfile?.dmxConnection === "helper";
  const dmxHelperClient = React.useMemo(
    () => (typeof window === "undefined" ? null : createBrowserDmxHelperClient()),
    [],
  );
  const bridgeBusy = bridgeAction !== "idle";
  const profileActive = activeProfile !== null;
  const scopeLocked = activeProfile !== null || bridgeBusy;
  const restartTimerRef = React.useRef<number | null>(null);
  const restartRequestIdRef = React.useRef(0);
  const failedRestartKeyRef = React.useRef<string | null>(null);
  const configDirtyRef = React.useRef(false);

  const markConfigDirtyIfActive = () => {
    if (activeProfile !== null) {
      configDirtyRef.current = true;
    }
  };

  React.useEffect(() => {
    if (bridgeConfig === null || activeProfile !== null || !isLoopbackHost(lanHost)) {
      return;
    }

    let cancelled = false;
    getPlatformLiveBridgeStatus(bridgeConfig)
      .then((status) => {
        if (cancelled) {
          return;
        }
        const preferredLanHost = choosePreferredLanHost(lanHost, status.suggestedLanHosts);
        if (preferredLanHost !== lanHost) {
          setLanHost(preferredLanHost);
        }
      })
      .catch(() => {
        return;
      });

    return () => {
      cancelled = true;
    };
  }, [activeProfile, bridgeConfig, lanHost]);

  // Refleja el estado REAL del live server: mientras hay perfil activo y bridge, poleamos /status y
  // chequeamos si NUESTRA sala sigue viva en el host. Si el host se durmió (Render free) o reinició,
  // pierde la sala en memoria → mostramos "sin conexión / detenido" en vez de un "Activo" mentiroso.
  React.useEffect(() => {
    // Sólo en cloud: el host remoto (Render/Fly) es el que se duerme/reinicia. El bridge local corre
    // en la propia PC, no se duerme, y no queremos un fetch de status por corrida en ese modo.
    if (
      activeBridgeConfig === null ||
      !activeCloudBridgeMode ||
      activeProfile === null ||
      activeHelperMode ||
      bridgeAction !== "idle"
    ) {
      return;
    }

    let cancelled = false;
    let timer: number | null = null;

    const myRoomAlive = (status: PlatformLiveBridgeStatus): boolean => {
      if (status.rooms === undefined) {
        return status.running; // host viejo sin info por sala
      }
      return status.rooms.some(
        (room) =>
          room.profile?.day === activeProfile.day &&
          room.profile?.platformId === activeProfile.platformId &&
          (room.profile?.tournamentId ?? undefined) === (activeProfile.tournamentId ?? undefined),
      );
    };

    const poll = async () => {
      try {
        const status = await getPlatformLiveBridgeStatus(activeBridgeConfig);
        if (cancelled) return;
        setLiveStatus(myRoomAlive(status) ? "running" : "stopped");
      } catch (_error) {
        if (!cancelled) setLiveStatus("unreachable");
      } finally {
        if (!cancelled) {
          timer = window.setTimeout(() => void poll(), 15000);
        }
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [activeProfile, activeBridgeConfig, activeCloudBridgeMode, activeHelperMode, bridgeAction]);

  React.useEffect(() => {
    if (!activeHelperMode || activeProfile === null || dmxHelperClient === null) return;
    let cancelled = false;
    let timer: number | null = null;

    const poll = async () => {
      try {
        const status = await dmxHelperClient.getStatus();
        if (cancelled) return;
        setHelperStatus(status);
        if (isDmxHelperRoomVerified(activeProfile, status)) {
          setLightsSyncing(false);
          setLightsError(null);
        } else if (status.connection !== "connected" || !isDmxHelperRoomAssigned(activeProfile, status)) {
          setLightsSyncing(false);
          setLightsError("Platform DMX Helper no confirmó la sala activa; la iluminación está sin conexión.");
        } else {
          // A valid room transport is independent from live-output authorization.
          // Verification/output failures belong to the live card below, not to the
          // transport "Luces sin conexión" error.
          setLightsSyncing(false);
          setLightsError(null);
        }
      } catch (error) {
        if (cancelled) return;
        setLightsSyncing(false);
        setLightsError(
          `Platform DMX Helper no está disponible; la iluminación está sin conexión. ${formatBridgeError(error)}`,
        );
      } finally {
        if (!cancelled) {
          timer = window.setTimeout(() => void poll(), 15_000);
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [activeHelperMode, activeProfile, dmxHelperClient]);

  // El candidato guardado es independiente del Helper: solo se restaura si el bridge confirma
  // que la sala exacta sigue activa. Las luces se degradan por su cuenta si el Helper no responde.
  React.useEffect(() => {
    if (activeProfile !== null || helperProfileCandidate === null) return;
    const candidateConfig = getPlatformLiveBridgeConfig(helperProfileCandidate.bridgeProviderId);
    if (candidateConfig === null) {
      clearHelperProfileCandidate();
      setBridgeError("La sala guardada no tiene un proveedor configurado; el perfil local no fue restaurado.");
      setLiveStatus("stopped");
      return;
    }
    let cancelled = false;
    let timer: number | null = null;
    const poll = async () => {
      try {
        const status = await getPlatformLiveBridgeStatus(candidateConfig);
        if (cancelled) return;
        const rooms = status.rooms;
        if (
          rooms !== undefined &&
          rooms.some(
            (room) =>
              room.profile?.day === helperProfileCandidate.day &&
              room.profile?.platformId === helperProfileCandidate.platformId &&
              (room.profile?.tournamentId ?? undefined) ===
                (helperProfileCandidate.tournamentId ?? undefined),
          )
        ) {
          setLiveStatus("running");
          activateProfile(helperProfileCandidate);
          return;
        }
        if (rooms !== undefined) {
          clearHelperProfileCandidate();
          setBridgeError("La sala guardada ya no está activa en el bridge; el perfil local no fue restaurado.");
          setLiveStatus("stopped");
          return;
        }
        setLiveStatus("unreachable");
        setBridgeError("El bridge no informó las salas activas; el perfil local sigue pendiente.");
      } catch (error) {
        if (cancelled) return;
        setLiveStatus("unreachable");
        setBridgeError(`El bridge no está disponible; el perfil local sigue pendiente. ${formatBridgeError(error)}`);
      }
      if (!cancelled) timer = window.setTimeout(() => void poll(), 15_000);
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [activeProfile, activateProfile, clearHelperProfileCandidate, helperProfileCandidate]);

  const buildProfileFromForm = React.useCallback(() => {
    const network = cloudBridgeMode ? getCloudNetworkDefaults() : { lanHost, appPort, livePort };
    const dmxConfig = makeDefaultDmxConfig(features.dmx);
    return makePlatformLiveProfile(
      makePlatformLiveProfileDraft({
        tournamentId: tournamentId ?? undefined,
        bridgeProviderId: bridgeMode ? selectedBridgeProviderId || undefined : undefined,
        day,
        platformId,
        lanHost: network.lanHost,
        appPort: network.appPort,
        livePort: network.livePort,
        dmxConfig,
        features,
      }),
      new Date().toISOString(),
    );
  }, [
    appPort,
    bridgeMode,
    cloudBridgeMode,
    day,
    features,
    lanHost,
    livePort,
    platformId,
    selectedBridgeProviderId,
    tournamentId,
  ]);

  const assignAndVerifyHelperRoom = React.useCallback(
    async (profile: PlatformLiveProfile, provider: PlatformLiveBridgeProvider): Promise<PlatformLiveProfile> => {
      if (dmxHelperClient === null) throw new Error("Platform DMX Helper is unavailable in this browser.");
      const helperProfile = withDmxHelperConnection(profile);
      const current = await dmxHelperClient.getStatus();
      setHelperStatus(current);
      const replacementRequired = current.desiredRoom !== null && !isDmxHelperRoomAssigned(helperProfile, current);
      if (
        replacementRequired &&
        !window.confirm(
          "Platform DMX Helper ya tiene otra sala asignada. ¿Confirmás reemplazarla por esta sala de Platform Live?",
        )
      ) {
        throw new Error("El reemplazo de la sala de Platform DMX Helper fue cancelado.");
      }
      const assigned = await dmxHelperClient.assignRoom(helperProfile, provider, replacementRequired);
      setHelperStatus(assigned);
      const verified = await dmxHelperClient.waitUntilRoomVerified(helperProfile, assigned);
      setHelperStatus(verified);
      setLiveStatus("running");
      return helperProfile;
    },
    [dmxHelperClient],
  );

  // Estado independiente de las luces: la sala del torneo vive aunque el Helper falle o esté lento.
  const [lightsSyncing, setLightsSyncing] = React.useState(false);
  const [lightsError, setLightsError] = React.useState<string | null>(null);

  const syncHelperLightsInBackground = React.useCallback(
    (helperProfile: PlatformLiveProfile, provider: PlatformLiveBridgeProvider): void => {
      setLightsSyncing(true);
      setLightsError(null);
      void assignAndVerifyHelperRoom(helperProfile, provider).then(
        () => {
          setLightsSyncing(false);
          setLightsError(null);
        },
        (error) => {
          setLightsSyncing(false);
          setLightsError(formatBridgeError(error));
        },
      );
    },
    [assignAndVerifyHelperRoom],
  );

  React.useEffect(() => {
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }

    if (
      activeBridgeConfig === null ||
      activeProfile === null ||
      bridgeAction !== "idle" ||
      !configDirtyRef.current
    ) {
      return undefined;
    }

    const nextProfile = buildProfileFromForm();
    const nextRuntimeKey = makePlatformLiveRuntimeKey(nextProfile);
    if (
      nextRuntimeKey === makePlatformLiveRuntimeKey(activeProfile) ||
      nextRuntimeKey === failedRestartKeyRef.current
    ) {
      return undefined;
    }

    restartTimerRef.current = window.setTimeout(() => {
      restartTimerRef.current = null;
      const requestId = restartRequestIdRef.current + 1;
      restartRequestIdRef.current = requestId;
      setBridgeError(null);
      setBridgeAction("restarting");

      const restart =
        activeHelperMode && activeHelperProvider !== null
          ? startPlatformLiveBridge(nextProfile, activeBridgeConfig).then(() => {
              const helperProfile = withDmxHelperConnection(nextProfile);
              setLiveStatus("running");
              syncHelperLightsInBackground(helperProfile, activeHelperProvider);
              return helperProfile;
            })
          : startPlatformLiveBridge(nextProfile, activeBridgeConfig).then(() => nextProfile);

      restart
        .then((activatedProfile) => {
          if (restartRequestIdRef.current !== requestId) {
            return;
          }
          failedRestartKeyRef.current = null;
          configDirtyRef.current = false;
          activateProfile(activatedProfile);
          setDevDetailsOpen(false);
        })
        .catch(async (error) => {
          if (restartRequestIdRef.current !== requestId) {
            return;
          }
          failedRestartKeyRef.current = nextRuntimeKey;
          setBridgeError(formatBridgeError(error));

          if (activeHelperMode) return;

          const errorStatus = getBridgeErrorStatus(error);
          if (errorStatus?.running === false) {
            deactivateProfile();
            return;
          }
          if (errorStatus?.running === true) {
            return;
          }

          try {
            const status = await getPlatformLiveBridgeStatus(activeBridgeConfig);
            if (status.running === false) {
              deactivateProfile();
            }
          } catch (_statusError) {
            return;
          }
        })
        .finally(() => {
          if (restartRequestIdRef.current === requestId) {
            setBridgeAction("idle");
          }
        });
    }, BRIDGE_RESTART_DEBOUNCE_MS);

    return () => {
      if (restartTimerRef.current !== null) {
        window.clearTimeout(restartTimerRef.current);
        restartTimerRef.current = null;
      }
    };
  }, [
    activeProfile,
    activeBridgeConfig,
    activeHelperMode,
    activeHelperProvider,
    activateProfile,
    bridgeAction,
    buildProfileFromForm,
    deactivateProfile,
    syncHelperLightsInBackground,
  ]);

  const handleDayChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (scopeLocked) {
      return;
    }
    const nextDay = Number(event.target.value);
    const nextMaxPlatforms = maxPlatformsForDay(meet, nextDay);
    const nextPlatform = Math.min(platformId, nextMaxPlatforms);
    setDay(nextDay);
    setPlatformId(nextPlatform);
  };

  const handlePlatformChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (scopeLocked) {
      return;
    }
    const nextPlatform = Number(event.target.value);
    setPlatformId(nextPlatform);
    setLivePort(4510 + nextPlatform);
  };

  const toggleFeature = (feature: keyof PlatformLiveFeatures) => {
    markConfigDirtyIfActive();
    const nextFeatures = {
      ...features,
      [feature]: !features[feature],
    };
    setFeatures({
      ...nextFeatures,
    });
  };

  const handleCopyLink = (key: string, url: string) => {
    const ok = copyToClipboard(url);
    if (ok) {
      setCopiedLink(key);
    }
  };

  const handleToggleQr = (key: string) => {
    setVisibleQr({
      ...visibleQr,
      [key]: !visibleQr[key],
    });
  };

  const handleBridgeProviderChange = (providerId: PlatformLiveBridgeProviderId) => {
    if (activeProfile !== null || bridgeBusy) {
      return;
    }
    setBridgeProvider(providerId);
  };

  const handleActivate = async () => {
    if (bridgeBusy || activeProfile !== null) {
      return;
    }
    // Aviso de "Siguiente" pendiente con la ventana en segundo plano: el permiso se pide en este click.
    requestOperatorNotificationPermission();

    const profile = buildProfileFromForm();

    setBridgeError(null);
    failedRestartKeyRef.current = null;
    configDirtyRef.current = false;
    if (helperCloudDmxMode && selectedHelperProvider !== null && bridgeConfig !== null) {
      // Primero el Platform Live Server; el Helper se sincroniza en segundo plano sin bloquear la sala.
      setBridgeAction("starting");
      try {
        await startPlatformLiveBridge(profile, bridgeConfig);
        const helperProfile = withDmxHelperConnection(profile);
        configDirtyRef.current = false;
        activateProfile(helperProfile);
        setLiveStatus("running");
        setDevDetailsOpen(false);
        syncHelperLightsInBackground(helperProfile, selectedHelperProvider);
      } catch (error) {
        setBridgeError(formatBridgeError(error));
      } finally {
        setBridgeAction("idle");
      }
      return;
    }
    if (bridgeConfig !== null) {
      setBridgeAction("starting");
      try {
        await startPlatformLiveBridge(profile, bridgeConfig);
        configDirtyRef.current = false;
        activateProfile(profile);
        setDevDetailsOpen(false);
      } catch (error) {
        setBridgeError(formatBridgeError(error));
      } finally {
        setBridgeAction("idle");
      }
      return;
    }

    activateProfile(profile);
    setDevDetailsOpen(false);
  };

  const handleDeactivate = async () => {
    if (bridgeBusy || activeProfile === null) {
      return;
    }

    setBridgeError(null);
    failedRestartKeyRef.current = null;
    configDirtyRef.current = false;
    if (activeHelperMode && dmxHelperClient !== null) {
      // Siempre se detiene primero el Platform Live Server; la limpieza del Helper es secundaria.
      setBridgeAction("stopping");
      if (activeBridgeConfig !== null) {
        try {
          await stopPlatformLiveBridge(activeBridgeConfig, activeProfile);
          configDirtyRef.current = false;
        } catch (error) {
          setBridgeError(formatBridgeError(error));
          setBridgeAction("idle");
          return;
        }
      }
      try {
        const status = await dmxHelperClient.clearRoom();
        setHelperStatus(status);
      } catch (error) {
        setBridgeError(formatBridgeError(error));
      } finally {
        // The room cleanup is best-effort. A temporarily unavailable Helper must not prevent the
        // operator from ending the Platform Live profile and continuing the tournament.
        setLiveStatus("stopped");
        setLightsSyncing(false);
        setLightsError(null);
        deactivateProfile();
        setBridgeAction("idle");
      }
      return;
    }
    if (activeBridgeConfig !== null) {
      setBridgeAction("stopping");
      try {
        await stopPlatformLiveBridge(activeBridgeConfig, activeProfile);
        configDirtyRef.current = false;
        deactivateProfile();
      } catch (error) {
        setBridgeError(formatBridgeError(error));
      } finally {
        setBridgeAction("idle");
      }
      return;
    }

    deactivateProfile();
  };

  // El server perdió la sala (se durmió/reinició): la re-creamos sin cambiar el perfil activo.
  const handleReactivate = async () => {
    if (activeProfile === null || activeBridgeConfig === null || bridgeBusy) {
      return;
    }
    setBridgeError(null);
    setBridgeAction("starting");
    try {
      await startPlatformLiveBridge(activeProfile, activeBridgeConfig);
      setLiveStatus("running");
      if (activeHelperMode && activeHelperProvider !== null) {
        syncHelperLightsInBackground(activeProfile, activeHelperProvider);
      }
    } catch (error) {
      setBridgeError(formatBridgeError(error));
    } finally {
      setBridgeAction("idle");
    }
  };

  // Reinicio manual del Platform Live Server: paramos y volvemos a levantar la sala con el MISMO perfil
  // (reinicio limpio — todos se reconectan solos). Sirve para recuperarse de un estado raro (p. ej.
  // "prendido pero no conectado"). Si la sala ya no existe (host reiniciado), ignoramos el stop y arrancamos.
  const handleRestart = async () => {
    if (activeProfile === null || activeBridgeConfig === null || bridgeBusy) {
      return;
    }
    setBridgeError(null);
    setBridgeAction("restarting");
    try {
      if (activeHelperMode && activeHelperProvider !== null) {
        await startPlatformLiveBridge(activeProfile, activeBridgeConfig);
        setLiveStatus("running");
        syncHelperLightsInBackground(activeProfile, activeHelperProvider);
        return;
      }
      try {
        await stopPlatformLiveBridge(activeBridgeConfig, activeProfile);
      } catch (_stopError) {
        /* la sala puede no existir ya (host reiniciado/dormido) — seguimos y la (re)creamos */
      }
      await startPlatformLiveBridge(activeProfile, activeBridgeConfig);
      setLiveStatus("running");
    } catch (error) {
      setBridgeError(formatBridgeError(error));
    } finally {
      setBridgeAction("idle");
    }
  };

  const activeUrls =
    activeProfile === null
      ? null
      : activeCloudBridgeMode && activeBridgeConfig !== null
        ? buildPlatformLiveUrlsForRemoteBridge(
            activeProfile,
            activeBridgeConfig.url,
            typeof window !== "undefined" ? window.location.origin : "",
          )
        : buildPlatformLiveUrls(activeProfile);
  const activeDmxBridgeCommand =
    activeProfile !== null &&
    activeUrls !== null &&
    activeCloudBridgeMode &&
    activeProfile.features.dmx &&
    !activeHelperMode
      ? buildPlatformDmxBridgeCommand(
          activeUrls.wsUrl,
          activeProfile.dmxConfig || makeDefaultDmxConfig(true),
        )
      : null;
  const handleOpenIntegratedJudgesPanel = () => {
    const panelUrl = activeUrls?.judgesPanelUrl;
    if (panelUrl === null || panelUrl === undefined || typeof window === "undefined") {
      return;
    }

    const confirmed = window.confirm(
      "El Panel Integrado de Jueces reemplazará las conexiones activas de J1, J2 y J3. " +
        "Al cerrarlo, cada dispositivo deberá reconectarse manualmente. ¿Abrir de todos modos?",
    );
    if (!confirmed) {
      return;
    }

    window.open(panelUrl, "_blank", "noopener,noreferrer");
  };
  const handleCopyIntegratedJudgesPanel = () => {
    const panelUrl = activeUrls?.judgesPanelUrl;
    if (panelUrl !== null && panelUrl !== undefined) {
      handleCopyLink("judgesPanel", panelUrl);
    }
  };
  const displayedBridgeProviderId = activeProfile?.bridgeProviderId || selectedBridgeProviderId;
  // Badge derivado del estado REAL del cloud host (no sólo del flag local `profileActive`).
  const liveServerDown =
    profileActive && activeCloudBridgeMode && (liveStatus === "stopped" || liveStatus === "unreachable");
  const liveBadge = !profileActive
    ? { text: "Inactivo", icon: faPowerOff, cls: styles.statusInactive }
    : !activeCloudBridgeMode
      ? { text: "Activo", icon: faCircleCheck, cls: styles.statusActive }
      : liveStatus === "running"
        ? { text: "Activo", icon: faCircleCheck, cls: styles.statusActive }
        : liveStatus === "unreachable"
          ? { text: "Sin conexión al server", icon: faPlug, cls: styles.statusInactive }
          : liveStatus === "stopped"
            ? { text: "Detenido en el server", icon: faPlug, cls: styles.statusInactive }
            : { text: "Verificando…", icon: faServer, cls: styles.statusInactive };

  return (
    <main className={styles.launcher} aria-label="Platform Live Launcher">
      <div className={styles.shell}>
        <header className={styles.topBar}>
          <div>
            <div className={styles.eyebrow}>
              <FontAwesomeIcon icon={faServer} className={styles.buttonIcon} />
              Platform Live Server
            </div>
            <h1 className={styles.title}>Platform Live Server</h1>
            <div className={styles.scopeLine}>
              Día {day} · Tarima {platformId}
              {bridgeMode && selectedBridgeProviderId !== null
                ? ` · Proveedor ${getPlatformLiveBridgeProviderLabel(selectedBridgeProviderId)}`
                : null}
            </div>
          </div>
          <div className={styles.headerStatus}>
            <span className={`${styles.statusBadge} ${liveBadge.cls}`}>
              <FontAwesomeIcon icon={liveBadge.icon} className={styles.buttonIcon} />
              {liveBadge.text}
            </span>
            {liveServerDown ? (
              <button
                type="button"
                className="btn btn-warning btn-sm"
                disabled={bridgeBusy}
                onClick={() => void handleReactivate()}
              >
                <FontAwesomeIcon icon={faBolt} className={styles.buttonIcon} />
                Reactivar
              </button>
            ) : null}
            {scopeLocked ? (
              <span className={styles.scopeLock}>
                <FontAwesomeIcon icon={faLock} className={styles.buttonIcon} />
                Scope bloqueado
              </span>
            ) : null}
          </div>
        </header>

        <div className={styles.layout}>
          <section className={styles.panel}>
            <div className={styles.panelHeader}>
              <div>
                <div className={styles.sectionKicker}>Configuración</div>
                <h2>Perfil de tarima</h2>
              </div>
            </div>

            {bridgeAction === "starting" ? (
              <div className={styles.bridgeProgress}>Iniciando Platform Live Server...</div>
            ) : null}
            {bridgeAction === "stopping" ? (
              <div className={styles.bridgeProgress}>Deteniendo Platform Live Server...</div>
            ) : null}
            {bridgeAction === "restarting" ? (
              <div className={styles.bridgeProgress}>Reiniciando Platform Live Server...</div>
            ) : null}
            {bridgeError !== null ? (
              <div className={styles.bridgeError} role="alert">
                {bridgeError}
              </div>
            ) : null}
            <div className={styles.scopeGrid}>
              <div className="form-group">
                <label htmlFor="platform-live-day">Día</label>
                <select
                  id="platform-live-day"
                  aria-label="Día"
                  className="form-control"
                  value={day}
                  disabled={scopeLocked}
                  onChange={handleDayChange}
                >
                  {makeDayOptions(meet).map((option) => (
                    <option key={option} value={option}>
                      Día {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="platform-live-platform">Tarima</label>
                <select
                  id="platform-live-platform"
                  aria-label="Tarima"
                  className="form-control"
                  value={platformId}
                  disabled={scopeLocked}
                  onChange={handlePlatformChange}
                >
                  {makePlatformOptions(meet, day).map((option) => (
                    <option key={option} value={option}>
                      Tarima {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.featureGrid}>
              <FeatureSwitch
                id="platform-live-judges"
                checked={features.judgeVoting}
                onChange={() => toggleFeature("judgeVoting")}
                label="Votación de jueces"
                description="Los tres jueces votan desde el celular con su enlace. El veredicto sale por mayoría (2 de 3)."
              />
              <FeatureSwitch
                id="platform-live-spectator"
                checked={features.spectatorDisplay}
                onChange={() => toggleFeature("spectatorDisplay")}
                label="Pantalla espectadores"
                description="Pantalla para el público con el atleta en tarima, el intento en curso, el cronómetro y las luces de los jueces."
              />
              <FeatureSwitch
                id="platform-live-streaming"
                checked={features.streamingDisplays === true}
                onChange={() => toggleFeature("streamingDisplays")}
                label="Streaming"
                description="Ficha del atleta y overlay transparente para sumar a la transmisión en OBS."
              />
              <FeatureSwitch
                id="platform-live-manual-verdict"
                checked={features.manualVerdictConfirmation === true}
                onChange={() => toggleFeature("manualVerdictConfirmation")}
                label="Confirmación manual del veredicto"
                description="El resultado se aplica recién cuando el operador toca «Confirmar»: hasta entonces los jueces pueden cambiar su voto y el público no ve las luces. Apagado, se aplica apenas votan los tres."
              />
              <FeatureSwitch
                id="platform-live-dmx"
                checked={features.dmx}
                onChange={() => toggleFeature("dmx")}
                label={
                  <>
                    <FontAwesomeIcon icon={faBolt} className={styles.buttonIcon} />
                    Luces DMX
                  </>
                }
                description="Enciende las luces de la tarima según el veredicto, a través de Platform DMX Helper."
              />
            </div>

            {features.dmx ? (
              <section className={styles.dmxSection} aria-label="Sistema de Luces">
                <div className={styles.subHeader}>
                  <h3>
                    <FontAwesomeIcon icon={faPlug} className={styles.buttonIcon} />
                    Sistema de Luces
                  </h3>
                </div>
                <p>
                  Las luminarias y el comportamiento Waiting viven en Platform DMX Helper. OpenLifter solo accede a su
                  consola y a la interfaz nativa del ETDMX.
                </p>
                <div className={styles.fixtureActions}>
                  <a
                    className={`btn btn-outline-primary btn-sm ${styles.actionButton}`}
                    href={`${DEFAULT_DMX_HELPER_URL}/`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir Platform DMX Helper
                    <FontAwesomeIcon icon={faUpRightFromSquare} className={styles.buttonIcon} />
                  </a>
                  <a
                    className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`}
                    href={ETDMX_NATIVE_WEB_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Abrir ETDMX PRO
                    <FontAwesomeIcon icon={faUpRightFromSquare} className={styles.buttonIcon} />
                  </a>
                </div>
                <small>Art-Net UDP 6454 · válido verde, nulo rojo.</small>
              </section>
            ) : null}

            {/* Lo que los organizadores no tocan. La red va primero y sigue disponible con la tarima activa
                (cambiarla reinicia el live server); el resto, solo antes de activar. */}
            {!cloudBridgeMode || activeProfile === null ? (
              <details
                className={styles.devDetails}
                aria-label="Configuraciones avanzadas del perfil"
                open={configDevDetailsOpen}
              >
                <summary
                  onClick={(event) => {
                    event.preventDefault();
                    setConfigDevDetailsOpen(!configDevDetailsOpen);
                  }}
                >
                  Configuraciones avanzadas
                </summary>
                <div className={styles.devContent}>
                  {!cloudBridgeMode ? (
                    <section className={styles.advancedGroup} aria-label="Red local">
                      <h4>Red local</h4>
                      <small>
                        Se completan solos: el host se detecta en la red y el puerto Live sigue a la tarima (Tarima 1 →
                        4511, Tarima 2 → 4512), así dos tarimas no chocan. Cambialos solo si otro programa ya usa ese
                        puerto.
                      </small>
                      <div className={styles.networkGrid}>
                        <div className="form-group">
                          <label htmlFor="platform-live-host">LAN host</label>
                          <input
                            id="platform-live-host"
                            aria-label="LAN host"
                            className="form-control"
                            value={lanHost}
                            onChange={(event) => {
                              markConfigDirtyIfActive();
                              setLanHost(event.target.value);
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="platform-live-app-port">App</label>
                          <input
                            id="platform-live-app-port"
                            aria-label="App port"
                            className="form-control"
                            type="number"
                            value={appPort}
                            onChange={(event) => {
                              markConfigDirtyIfActive();
                              setAppPort(Number(event.target.value));
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="platform-live-port">Live</label>
                          <input
                            id="platform-live-port"
                            aria-label="Live port"
                            className="form-control"
                            type="number"
                            value={livePort}
                            onChange={(event) => {
                              markConfigDirtyIfActive();
                              setLivePort(Number(event.target.value));
                            }}
                          />
                        </div>
                      </div>
                    </section>
                  ) : null}
                  {activeProfile === null ? (
                    <>
                      {renderBridgeModeDetails(
                        bridgeMode,
                        bridgeConfig?.url,
                        bridgeProviderOptions,
                        selectedBridgeProviderId,
                        bridgeBusy,
                        handleBridgeProviderChange,
                      )}
                      <RecordTestToolsToggle enabled={recordTestToolsEnabled} onChange={setRecordTestTools} />
                      <RegistrationPaginationToggle
                        enabled={registrationPaginationEnabled}
                        onChange={setRegistrationPagination}
                      />
                      <IntegratedJudgesPanelAction
                        enabled={false}
                        copied={false}
                        onOpen={() => undefined}
                        onCopy={() => undefined}
                      />
                    </>
                  ) : null}
                </div>
              </details>
            ) : null}

            <div className={styles.launchActions}>
              <button
                type="button"
                className={`btn btn-primary ${styles.actionButton}`}
                disabled={bridgeBusy || activeProfile !== null}
                onClick={handleActivate}
              >
                <FontAwesomeIcon icon={faPowerOff} className={styles.buttonIcon} />
                Activar
              </button>
              {activeProfile !== null && activeBridgeConfig !== null ? (
                <button
                  type="button"
                  className={`btn btn-outline-secondary ${styles.actionButton}`}
                  disabled={bridgeBusy}
                  onClick={() => void handleRestart()}
                >
                  <FontAwesomeIcon icon={faRotateRight} className={styles.buttonIcon} />
                  Reiniciar
                </button>
              ) : null}
              {activeProfile !== null ? (
                <button
                  type="button"
                  className={`btn btn-outline-danger ${styles.actionButton}`}
                  disabled={bridgeBusy}
                  onClick={handleDeactivate}
                >
                  <FontAwesomeIcon icon={faPowerOff} className={styles.buttonIcon} />
                  Desactivar
                </button>
              ) : null}
            </div>
          </section>

          <section className={styles.activePanel}>
            {activeProfile === null || activeUrls === null ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faServer} className={styles.emptyIcon} />
                <div>Sin Platform Live Profile activo.</div>
              </div>
            ) : (
              <div>
                <div className={styles.activeHeader}>
                  <div>
                    <div className={styles.sectionKicker}>Accesos activos</div>
                    <h2>Tarima {activeProfile.platformId}</h2>
                  </div>
                  <span className={`${styles.statusBadge} ${liveBadge.cls}`}>
                    <FontAwesomeIcon icon={liveBadge.icon} className={styles.buttonIcon} />
                    {liveBadge.text}
                  </span>
                </div>

                {liveServerDown ? (
                  <div className="alert alert-warning py-2 small mt-2 mb-0">
                    El servidor de la tarima no está respondiendo (puede haberse dormido o reiniciado). Los jueces y la
                    pantalla no se conectan hasta que toques <strong>Reactivar</strong> arriba.
                  </div>
                ) : null}

                {activeHelperMode ? (
                  <section className={styles.dmxSection} aria-label="Estado del Sistema de Luces">
                    <div className={styles.subHeader}>
                      <h3>
                        <FontAwesomeIcon icon={faBolt} className={styles.buttonIcon} />
                        Sistema de Luces
                      </h3>
                    </div>
                    <div
                      className={
                        helperStatus?.live?.status === "degraded" || (!lightsSyncing && lightsError !== null)
                          ? styles.bridgeError
                          : styles.modeAuto
                      }
                      role="status"
                    >
                      <strong>
                        {lightsSyncing
                          ? "Sin autorización — sincronizando Helper"
                          : lightsError !== null
                            ? "Sin conexión — blackout"
                            : helperStatus?.live?.status === "degraded"
                              ? "Salida degradada"
                              : helperStatus?.live?.status === "verifying"
                                ? "Verificando"
                                : helperStatus?.live?.enabled === false
                                  ? "Sin autorización — blackout"
                                  : helperStatus?.live?.status === "active"
                                    ? "Activo"
                                    : helperStatus?.live?.status === "offline"
                                      ? "Offline"
                                      : helperStatus?.connection !== "connected"
                                        ? "Sin conexión — blackout"
                                        : "Sin autorización — blackout"}
                      </strong>
                      {helperStatus?.live?.status === "degraded" && helperStatus.live.lastError ? (
                        <span>{helperStatus.live.lastError}</span>
                      ) : null}
                      {!lightsSyncing && lightsError !== null ? <span>Luces sin conexión: {lightsError}</span> : null}
                      {!lightsSyncing && lightsError !== null && activeHelperProvider !== null ? (
                        <button
                          type="button"
                          className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`}
                          disabled={bridgeBusy}
                          onClick={() => syncHelperLightsInBackground(activeProfile, activeHelperProvider)}
                        >
                          <FontAwesomeIcon icon={faRotateRight} className={styles.buttonIcon} />
                          Reintentar luces
                        </button>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                <div className={styles.roleGrid} aria-label="Enlaces por rol">
                  <ClientLinkCard
                    label="Levantamientos"
                    url={activeUrls.liftingUrl}
                    qrVisible={visibleQr.lifting === true}
                    copied={copiedLink === "lifting"}
                    onCopy={() => handleCopyLink("lifting", activeUrls.liftingUrl)}
                    onToggleQr={() => handleToggleQr("lifting")}
                  />
                  {activeProfile.features.judgeVoting
                    ? JUDGE_POSITIONS.map((position: JudgePosition) => {
                        const url = activeUrls.judgeUrls[position] || "";
                        return (
                          <ClientLinkCard
                            key={position}
                            label={JUDGE_LABELS[position]}
                            url={url}
                            qrVisible={visibleQr[position] === true}
                            copied={copiedLink === position}
                            onCopy={() => handleCopyLink(position, url)}
                            onToggleQr={() => handleToggleQr(position)}
                          />
                        );
                      })
                    : null}
                  {activeProfile.features.spectatorDisplay && activeUrls.spectatorUrl !== null ? (
                    <ClientLinkCard
                      label="Pantalla espectadores"
                      url={activeUrls.spectatorUrl}
                      qrVisible={visibleQr.spectator === true}
                      copied={copiedLink === "spectator"}
                      onCopy={() => handleCopyLink("spectator", activeUrls.spectatorUrl || "")}
                      onToggleQr={() => handleToggleQr("spectator")}
                    />
                  ) : null}
                  {activeProfile.features.streamingDisplays && activeUrls.streamingUrls !== null ? (
                    <>
                      <ClientLinkCard
                        label="Ficha del atleta"
                        url={activeUrls.streamingUrls.athlete}
                        qrVisible={visibleQr.streamingAthlete === true}
                        copied={copiedLink === "streamingAthlete"}
                        onCopy={() => handleCopyLink("streamingAthlete", activeUrls.streamingUrls?.athlete || "")}
                        onToggleQr={() => handleToggleQr("streamingAthlete")}
                      />
                      <ClientLinkCard
                        label="Overlay de transmisión"
                        url={withOverlayBackground(activeUrls.streamingUrls.overlay, overlayBackground)}
                        qrVisible={visibleQr.streamingOverlay === true}
                        copied={copiedLink === "streamingOverlay"}
                        onCopy={() =>
                          handleCopyLink(
                            "streamingOverlay",
                            withOverlayBackground(activeUrls.streamingUrls?.overlay || "", overlayBackground),
                          )
                        }
                        onToggleQr={() => handleToggleQr("streamingOverlay")}
                      />
                      <div className="form-group">
                        <label htmlFor="platform-live-overlay-background">Fondo del overlay</label>
                        <select
                          id="platform-live-overlay-background"
                          aria-label="Fondo del overlay"
                          className="form-control"
                          value={overlayBackground}
                          onChange={(event) => changeOverlayBackground(event.target.value as OverlayBackground)}
                        >
                          <option value="blanco">Blanco</option>
                          <option value="transparente">Transparente (fuente de navegador de OBS)</option>
                        </select>
                      </div>
                    </>
                  ) : null}
                </div>

                <details className={styles.devDetails} aria-label="Datos para desarrolladores" open={devDetailsOpen}>
                  <summary
                    onClick={(event) => {
                      event.preventDefault();
                      setDevDetailsOpen(!devDetailsOpen);
                    }}
                  >
                    Datos para desarrolladores
                  </summary>
                  {devDetailsOpen ? (
                    <div className={styles.devContent}>
                      {renderBridgeModeDetails(
                        bridgeMode,
                        activeBridgeConfig?.url,
                        bridgeProviderOptions,
                        displayedBridgeProviderId,
                        true,
                        handleBridgeProviderChange,
                      )}
                      <RecordTestToolsToggle enabled={recordTestToolsEnabled} onChange={setRecordTestTools} />
                      <RegistrationPaginationToggle
                        enabled={registrationPaginationEnabled}
                        onChange={setRegistrationPagination}
                      />
                      <IntegratedJudgesPanelAction
                        enabled={activeUrls.judgesPanelUrl !== null}
                        copied={copiedLink === "judgesPanel"}
                        onOpen={handleOpenIntegratedJudgesPanel}
                        onCopy={handleCopyIntegratedJudgesPanel}
                      />
                      {activeDmxBridgeCommand !== null ? (
                        <>
                          <p>Ejecutá este comando en la PC conectada al ETDMX y dejalo abierto durante el torneo.</p>
                          <pre aria-label="Platform DMX Bridge command">{activeDmxBridgeCommand}</pre>
                          <button
                            type="button"
                            className={`btn btn-outline-secondary btn-sm ${styles.actionButton}`}
                            onClick={() => handleCopyLink("dmxCommand", activeDmxBridgeCommand)}
                          >
                            <FontAwesomeIcon icon={faCopy} className={styles.buttonIcon} />
                            Copiar comando DMX
                          </button>
                          {copiedLink === "dmxCommand" ? (
                            <div className={styles.copyFeedback}>Comando copiado en el portapapeles</div>
                          ) : null}
                        </>
                      ) : activeHelperMode ? (
                        <p>
                          Platform DMX Helper administra esta sala y la recupera automáticamente; no hace falta copiar
                          un comando DMX.
                        </p>
                      ) : activeCloudBridgeMode ? (
                        <p>DMX está desactivado para esta sala; no hace falta ejecutar un proceso local.</p>
                      ) : (
                        <pre aria-label="Platform Live command">{buildPlatformLiveCommand(activeProfile)}</pre>
                      )}
                      <p>WS: {activeUrls.wsUrl}</p>
                    </div>
                  ) : null}
                </details>
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

const mapStateToProps = (state: GlobalState): StateProps => ({
  meet: state.meet,
  lifting: state.lifting,
  activeProfile: state.wrpfLive.activeProfile,
  helperProfileCandidate: state.wrpfLive.helperProfileCandidate,
  tournamentId: state.wrpfSession.tournamentId,
  recordTestToolsEnabled: state.wrpfLive.developerSettings.recordTestToolsEnabled,
  registrationPaginationEnabled: state.wrpfLive.developerSettings.registrationPaginationEnabled,
  bridgeProviderId: state.wrpfLive.developerSettings.bridgeProviderId,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  activateProfile: (profile) => dispatch(activatePlatformLiveProfile(profile)),
  clearHelperProfileCandidate: () => dispatch(clearHelperProfileCandidate()),
  deactivateProfile: () => dispatch(deactivatePlatformLiveProfile()),
  setRecordTestTools: (enabled) => dispatch(setRecordTestToolsEnabled(enabled)),
  setRegistrationPagination: (enabled) => dispatch(setRegistrationPaginationEnabled(enabled)),
  setBridgeProvider: (providerId) => dispatch(setPlatformLiveBridgeProvider(providerId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlatformLiveLauncherContainer);
